import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { Button, Modal, Tabs, Tab } from 'react-bootstrap';
import { generalPrompts } from '../../config/generalPrompts';
import { ReactComponent as GeneralIcon } from '../../images/list-task.svg';
import { ReactComponent as RoleIcon } from '../../images/bullseye.svg';
import PromptDescriptionModal from '../PromptDescriptionModal';
import { Prompt } from '../UserProfile/PromptDataTabContent';
import PromptHelpNotice from './PromptHelpNotice';
import { getPromptRecommendationBadge } from '../../utils/promptRecommendationBadge';

interface PromptSelectionModalProps {
  jobIndustry: string;
  jobTitle: string;
  level: string;
  roleSpecificPrompts?: string[];
  rolePromptPoolSize?: number;
  rolePromptsLoading?: boolean;
  rolePromptsRefreshing?: boolean;
  canGenerateRolePrompts?: boolean;
  onRefreshRolePrompts?: () => void | Promise<void>;
  onModalClose?: () => void;
  onPromptSelect: (prompt: string, options?: { openVideo?: boolean }) => void;
  onEnsurePromptInSet?: (prompt: string) => number | null;
  videoFiles?: { [key: number]: File };
  setVideoFiles?: React.Dispatch<React.SetStateAction<{ [key: number]: File }>>;
  descriptions?: { [key: number]: string };
  setDescriptions?: React.Dispatch<React.SetStateAction<{ [key: number]: string }>>;
  editingIndex: number | null;
  setEditingIndex: React.Dispatch<React.SetStateAction<number | null>>;
  promptArray: string[];
  onAddVideo?: (arg: string | Prompt) => void;
  onClearVideoAtIndex?: (index: number) => void;
}

const PromptSelectionModal: React.FC<PromptSelectionModalProps> = ({
  roleSpecificPrompts = [],
  rolePromptPoolSize = 0,
  rolePromptsLoading = false,
  rolePromptsRefreshing = false,
  canGenerateRolePrompts = true,
  onRefreshRolePrompts,
  onModalClose,
  onPromptSelect,
  onEnsurePromptInSet,
  videoFiles,
  setVideoFiles,
  descriptions,
  setDescriptions,
  editingIndex,
  setEditingIndex,
  promptArray,
  onAddVideo: _onAddVideo,
  onClearVideoAtIndex,
}) => {
  const [activeTab, setActiveTab] = useState<'general' | 'role-specific'>('general');
  const [expandedPromptId, setExpandedPromptId] = useState<string | null>(null);
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState<boolean>(false);
  const [currentDescription, setCurrentDescription] = useState<string>('');

  const handleOpenDescriptionModal = (prompt: string, promptIndex: number | null) => {
    if (!setDescriptions) {
      return;
    }

    let idx = promptIndex;
    if (idx === null) {
      if (onEnsurePromptInSet) {
        idx = onEnsurePromptInSet(prompt);
        if (idx === null) {
          return;
        }
      } else {
        toast.info('Use “Use Prompt” first, then add a description.');
        return;
      }
    }

    setEditingIndex(idx);
    setCurrentDescription(descriptions?.[idx] || '');
    setIsDescriptionModalOpen(true);
  };

  const getPromptIndex = (prompt: string): number | null => {
    const index = promptArray.findIndex((p) => p === prompt);
    return index >= 0 ? index : null;
  };

  const isDuplicateSelection = (prompt: string, targetIndex: number | null): boolean => {
    const existingIdx = getPromptIndex(prompt);
    if (existingIdx === null || existingIdx < 0) return false;
    if (targetIndex !== null && existingIdx === targetIndex) return false;
    if (editingIndex !== null && existingIdx === editingIndex) return false;
    return true;
  };

  const confirmVideoResetIfNeeded = (prompt: string) => {
    if (editingIndex === null) return true;

    const currentPrompt = promptArray[editingIndex];
    const promptChanged = currentPrompt !== prompt;
    const hasCurrentVideo = Boolean(videoFiles?.[editingIndex]);

    if (promptChanged && hasCurrentVideo) {
      const confirmed = window.confirm(
        'Changing this prompt will remove the existing video. Continue?'
      );

      if (!confirmed) {
        return false;
      }

      setVideoFiles?.((prev) => {
        const next = { ...prev };
        delete next[editingIndex];
        return next;
      });
      onClearVideoAtIndex?.(editingIndex);
    }

    return true;
  };

  const resolveTargetIndex = (promptIndex: number | null): number | null => {
    if (editingIndex !== null && editingIndex >= 0) {
      return editingIndex;
    }
    return promptIndex;
  };

  const handleUsePrompt = (prompt: string, promptIndex: number | null) => {
    if (!confirmVideoResetIfNeeded(prompt)) {
      return;
    }

    const targetIndex = resolveTargetIndex(promptIndex);
    if (isDuplicateSelection(prompt, targetIndex)) {
      toast.info('This prompt is already in your set.');
      return;
    }
    if (targetIndex !== null) {
      setEditingIndex(targetIndex);
    }
    onPromptSelect(prompt);
    onModalClose?.();
  };

  const handleQuickVideo = (prompt: string, promptIndex: number | null) => {
    if (!confirmVideoResetIfNeeded(prompt)) {
      return;
    }

    const targetIndex = resolveTargetIndex(promptIndex);
    if (targetIndex !== null) {
      setEditingIndex(targetIndex);
    }
    onPromptSelect(prompt, { openVideo: true });
    onModalClose?.();
  };

  const renderPromptCard = (
    prompt: string,
    index: number,
    listKey: 'general' | 'role-specific'
  ) => {
    const promptIndex = getPromptIndex(prompt);
    const usedElsewhere = isDuplicateSelection(prompt, promptIndex);
    const hasVideo = promptIndex !== null && Boolean(videoFiles?.[promptIndex]);
    const hasDescription = promptIndex !== null && Boolean(descriptions?.[promptIndex]?.trim());
    const cardId = `${listKey}-${index}`;
    const isExpanded = expandedPromptId === cardId;
    const badge = getPromptRecommendationBadge(index);
    const badgeClass =
      badge?.variant === 'most'
        ? 'prompt-card__badge prompt-card__badge--top'
        : 'prompt-card__badge';

    return (
      <div
        key={cardId}
        className={`prompt-card ${isExpanded ? 'prompt-card--active' : ''}`}
        onClick={() => {
          setExpandedPromptId(isExpanded ? null : cardId);
          if (editingIndex === null) {
            setEditingIndex(promptIndex);
          }
        }}
      >
        <div className="prompt-card__recommend-row">
          {badge ? <span className={badgeClass}>{badge.label}</span> : <span />}
          {hasVideo && <span className="prompt-card__status">Video added</span>}
        </div>

        <div className="prompt-card__top">
          <span className="prompt-card__text">{prompt}</span>
        </div>

        {isExpanded && (
          <div className="prompt-card__actions">
            {usedElsewhere ? (
              <span className="prompt-card__status">Already in your set</span>
            ) : null}
            <Button
              variant="success"
              size="sm"
              disabled={usedElsewhere}
              onClick={(e) => {
                e.stopPropagation();
                handleUsePrompt(prompt, promptIndex);
              }}
            >
              Use Prompt
            </Button>

            <Button
              variant="outline-success"
              size="sm"
              disabled={usedElsewhere}
              onClick={(e) => {
                e.stopPropagation();
                handleQuickVideo(prompt, promptIndex);
              }}
            >
              {hasVideo ? 'Replace Video' : 'Add Video'}
            </Button>

            <Button
              variant="outline-primary"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                handleOpenDescriptionModal(prompt, promptIndex);
              }}
              disabled={!setDescriptions}
              title="Add or edit description (optional)"
            >
              {hasDescription ? 'Edit Description' : 'Add Description'}
            </Button>
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      <Modal.Header closeButton>
        <Modal.Title>Build Your Prompt Set</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <div className="prompt-picker-hero">
          <div className="prompt-picker-hero__left">
            <h6 className="mb-1">
              {editingIndex !== null ? 'Replace this prompt' : 'Add a prompt'}
            </h6>
            <p className="mb-0">
              Pick a question for your set. Video, description, and documents are optional until you
              submit.
            </p>
          </div>
          <div className="prompt-picker-hero__right">
            <GeneralIcon width={18} height={18} />
            <RoleIcon width={18} height={18} />
          </div>
        </div>

        <PromptHelpNotice />

        <div className="modal-scrollable-content">
          <Tabs
            activeKey={activeTab}
            onSelect={(k) => {
              setActiveTab(k as 'general' | 'role-specific');
            }}
          >
            <Tab eventKey="general" title="General Prompts">
              {generalPrompts.map((prompt, index) => renderPromptCard(prompt, index, 'general'))}
            </Tab>

            <Tab eventKey="role-specific" title="Role-Specific Prompts">
              {onRefreshRolePrompts ? (
                <div className="prompt-picker-role-toolbar">
                  <Button
                    variant="outline-success"
                    size="sm"
                    disabled={
                      !canGenerateRolePrompts || rolePromptsRefreshing || rolePromptsLoading
                    }
                    onClick={() => void onRefreshRolePrompts()}
                  >
                    {rolePromptsRefreshing
                      ? 'Generating…'
                      : rolePromptPoolSize > 0
                        ? 'Refresh role-specific prompts'
                        : 'Generate role-specific prompts'}
                  </Button>
                </div>
              ) : null}

              {rolePromptsLoading ? (
                <p className="text-muted mt-3">Loading your role-specific prompts…</p>
              ) : null}

              {!rolePromptsLoading && roleSpecificPrompts.length > 0 ? (
                roleSpecificPrompts.map((prompt, index) =>
                  renderPromptCard(prompt, index, 'role-specific')
                )
              ) : !rolePromptsLoading ? (
                <p className="text-muted mt-3">
                  {onRefreshRolePrompts
                    ? canGenerateRolePrompts
                      ? 'No role-specific prompts yet. Generate a personalized list for your target role.'
                      : 'Add a target role to your profile before generating role-specific prompts.'
                    : 'Role-specific prompts are generated after you complete Step 1. Go back and click Next to generate your personalized list.'}
                </p>
              ) : null}
            </Tab>
          </Tabs>
        </div>
      </Modal.Body>

      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onModalClose}>
          Close
        </Button>
      </Modal.Footer>

      <Modal
        show={isDescriptionModalOpen}
        onHide={() => setIsDescriptionModalOpen(false)}
        size="lg"
        centered
      >
        <PromptDescriptionModal
          onModalClose={() => setIsDescriptionModalOpen(false)}
          setDescriptions={setDescriptions}
          currentDescription={currentDescription}
          setCurrentDescription={setCurrentDescription}
          editingIndex={editingIndex}
        />
      </Modal>
    </>
  );
};

export default PromptSelectionModal;
